import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRequest } from '~/lib/useRequest';
import { config } from '~/config';
import { PatchProgramResponse, PatchProgramRequestBody } from '@ab/ams-school-api/types/private/v1';
import { hasKey } from '~/utils';

export type PatchProgramParams = PatchProgramRequestBody['data']['attributes'];

export function usePatchProgram(id: string) {
  const request = useRequest();
  const queryClient = useQueryClient();

  const { isLoading, error, mutateAsync } = useMutation(['patchProgram', id], async (payload: PatchProgramParams) => {
    const response = await request<PatchProgramResponse>(`${config.apiHost}/private/v1/programs/${id}`, {
      method: 'PATCH',
      body: {
        data: {
          id,
          type: 'program',
          attributes: payload,
        },
      },
      isExpectedResponse,
    });

    // Invalidate and refetch program data
    await queryClient.invalidateQueries(['program', id]);

    return response;
  });

  return {
    patchProgram: mutateAsync,
    patchProgramError: error,
    isPatchingProgram: isLoading,
  };
}

function isExpectedResponse(res: unknown): res is PatchProgramResponse {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}
